/**
 * `/promo/:code` — the landing page a promo link points at (promo plan P4).
 *
 * ── Why there is a picker instead of an automatic redeem ─────────────
 *
 * A grant attaches to ONE tenant, and a redemption is one-shot: the code's
 * per-tenant guard means the same code cannot later be moved to the org the
 * person actually meant. Only a platform admin can revoke a grant, so a wrong
 * automatic choice is a support ticket. The caller picks, every time — even
 * when they have exactly one eligible tenant, because "this is where your
 * promotion is going" is the whole point of the screen.
 *
 * ── Which tenants are offered ───────────────────────────────────────
 *
 * ALL of them, ordered so the likely-eligible come first. This used to offer
 * only tenants where the caller's ROLE was owner or admin, described as
 * mirroring the server's authority — which it did not. `manage_billing:write`
 * is grantable per role, per team and per user, so an org that had deliberately
 * granted it to somebody else simply never appeared, and no message explained
 * why. The server gate (`requireBillingAdmin`) is the authority; this list only
 * orders the options. See `BILLING_ROLES_BY_DEFAULT` below.
 *
 * ── Why the failure copy says so little ─────────────────────────────
 *
 * The server answers identically for unknown, revoked, expired and exhausted
 * codes, on purpose — anything else is a code-validity oracle (codes are bearer
 * tokens, and vanity ones are guessable). This page must not invent a
 * distinction the server refused to make, so it renders the server's message
 * as-is rather than mapping status codes to friendlier guesses.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from '@papan/i18n'
import { useAuth } from '../hooks/useAuth'
import { billingApi } from '@papan/billing/client'
import { orgsApi } from '@papan/orgs/client'

interface Choice {
  id: string
  name: string
  isPersonal: boolean
}

/**
 * Roles that hold `manage_billing:write` **by default**.
 *
 * The comment here used to say this "mirrors the same authority" the server
 * enforces. It does not — it mirrors the capability's DEFAULTS. `manage_billing`
 * is grantable per role, per team and per user, so an org that deliberately gave
 * it to someone outside these two roles was silently absent from this picker
 * while the server would have accepted the redemption.
 *
 * It stays as the FALLBACK, not the answer: `listMyTenants` returns a role per
 * membership and nothing else, so a defaults-based guess is all this list can
 * make without a per-tenant capability round trip for every org the user belongs
 * to. What changed is that a guess no longer decides — the tenant is offered,
 * and the server's own gate is what refuses. Erring toward SHOWING an option is
 * right here: a refusal is legible ("this code cannot be redeemed"), whereas an
 * org that never appears is invisible and unexplainable.
 */
const BILLING_ROLES_BY_DEFAULT = new Set(['owner', 'admin'])

export function PromoRedeemPage() {
  // Localized (D8). Every string on this page was a hardcoded English literal
  // — on a customer-facing page reached from a marketing link, in a product
  // that ships four languages. The ONE exception is the server's failure
  // message, rendered verbatim; see the header for why this page must not
  // invent a distinction the server deliberately refused to make.
  const { t } = useTranslation('billing')
  const { code } = useParams<{ code: string }>()
  const { isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()

  const [choices, setChoices] = useState<Choice[] | null>(null)
  const [picked, setPicked] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  // Unauthenticated → sign in and come straight back. The code stays in the
  // URL, so a signup detour loses nothing.
  useEffect(() => {
    if (isLoading || isAuthenticated || !code) return
    navigate(`/login?redirect=${encodeURIComponent(`/promo/${code}`)}`, { replace: true })
  }, [isAuthenticated, isLoading, code, navigate])

  useEffect(() => {
    if (!isAuthenticated) return
    let cancelled = false
    orgsApi.listMyTenants()
      .then(({ tenants }) => {
        if (cancelled) return
        // ORDERED by likely eligibility, not FILTERED by it.
        //
        // This used to drop every tenant whose role was outside
        // `BILLING_ROLES_BY_DEFAULT`, which silently hid an org that had
        // deliberately granted `manage_billing:write` to someone else — the
        // grant existed, the server would have honoured it, and the only thing
        // refusing was this list.
        //
        // Resolving the real capability here would mean one round trip PER ORG
        // the user belongs to, on a page whose whole job is one POST. So the
        // guess is demoted to a SORT: the likely-eligible are offered first and
        // preselected, everything else is still reachable, and the server's own
        // gate is what decides. A refusal is legible; an org that never appears
        // is not.
        const rank = (t: { type: string; emails: { role: string }[] }) =>
          t.type === 'personal' || t.emails.some((e) => BILLING_ROLES_BY_DEFAULT.has(e.role)) ? 0 : 1
        const ordered = [...tenants]
          .sort((a, b) => rank(a) - rank(b))
          .map((t) => ({ id: t.id, name: t.name, isPersonal: t.type === 'personal' }))
        setChoices(ordered)
        setPicked(ordered[0]?.id ?? '')
      })
      .catch((e: any) => { if (!cancelled) setError(e?.message || t('redeem.loadFailed')) })
    return () => { cancelled = true }
  }, [isAuthenticated, t])

  const redeem = useCallback(async () => {
    if (!code || !picked) return
    setBusy(true)
    setError(null)
    try {
      const { grant } = await billingApi.redeemPromoCode(code, picked)
      setDone(grant.tenantId)
      // Land on the Billing page of the tenant that received it.
      //
      // ⚠️ This comment used to end "where the grant now shows — the
      // confirmation is the state, not a toast." The second half is the design
      // and still stands; the FIRST half is not true yet. No customer-facing
      // surface renders a promo grant: it becomes visible only as an "Also
      // funded this period" line AFTER it has been spent. So the confirmation
      // this flow deliberately declined to give as a toast is currently not
      // given at all (finding D9/M5). The interim `done` state above is what
      // carries it; the destination becomes correct when the grants view lands
      // (plan item 4.5).
      const chosen = choices?.find((c) => c.id === grant.tenantId)
      const prefix = chosen?.isPersonal ? '/~' : `/t/${grant.tenantId}`
      // Settings → Billing, not the Usage & Billing shelf: that shelf is
      // platform-only now, and the redeemer is an ordinary tenant owner.
      setTimeout(() => navigate(`${prefix}/settings/subscription`, { replace: true }), 1200)
    } catch (e: any) {
      // Rendered verbatim; see the header on why this page adds no guesses.
      setError(e?.message || t('redeem.failed'))
    } finally {
      setBusy(false)
    }
  }, [code, picked, choices, navigate, t])

  if (isLoading || !isAuthenticated) {
    return <Centered><p className="themed-text">{t('common:loading')}</p></Centered>
  }

  if (done) {
    return (
      <Centered>
        <p className="text-green-400">{t('redeem.done')}</p>
      </Centered>
    )
  }

  return (
    <Centered>
      <div className="w-full max-w-sm space-y-4">
        <div className="space-y-1 text-center">
          <h1 className="themed-text text-lg font-medium">{t('redeem.title')}</h1>
          <p className="themed-text-muted text-sm">
            {t('redeem.codeLabel')} <span className="font-mono themed-text">{code}</span>
          </p>
        </div>

        {choices === null && <p className="themed-text-muted text-sm text-center">{t('redeem.loadingOrgs')}</p>}

        {choices !== null && choices.length === 0 && (
          // Every user has a personal tenant, so this is only reachable if the
          // tenant list itself failed to resolve — say that rather than
          // implying the promotion is at fault.
          <p className="themed-text-muted text-sm text-center">
            {t('redeem.noEligible')}
          </p>
        )}

        {choices !== null && choices.length > 0 && (
          <>
            <label className="block space-y-1">
              <span className="themed-text-secondary text-xs">{t('redeem.applyTo')}</span>
              <select
                value={picked}
                disabled={busy}
                onChange={(e) => setPicked(e.target.value)}
                aria-label={t('redeem.applyTo')}
                className="themed-input border rounded w-full px-2 py-1.5 text-sm outline-none focus:border-blue-500 disabled:opacity-50"
              >
                {/* `c`, not `t` — the map parameter shadowed the translate function. */}
                {choices.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.isPersonal ? t('redeem.personalSuffix', { name: c.name }) : c.name}
                  </option>
                ))}
              </select>
            </label>

            {/* Stated before the button, not after the fact: the choice is not
                reversible by the person making it. */}
            <p className="themed-text-muted text-[11px]">
              {t('redeem.oneTime')}
            </p>

            <button
              onClick={redeem}
              disabled={busy || !picked}
              className="w-full px-3 py-2 text-sm rounded themed-btn-primary disabled:opacity-50"
            >
              {busy ? t('redeem.redeeming') : t('redeem.redeem')}
            </button>
          </>
        )}

        {error && <p className="text-sm text-red-400 text-center">{error}</p>}
      </div>
    </Centered>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-screen flex items-center justify-center themed-bg-primary px-4">
      {children}
    </div>
  )
}
